import { useEffect } from 'react';
import { useDeMinimisCheck } from '@shared/hooks/useDeMinimisCheck';

interface CheckState {
  organizationNumber: string;
  status: 'idle' | 'checking' | 'verified' | 'unavailable';
  previousSupport: number;
  requestedAmount: number;
  totalAmount: number;
  difference: number;
  isOverThreshold: boolean;
  checkedAt?: string;
  error?: string;
}

interface Props {
  organizationNumber: string;
  requestedAmount: number;
  threshold: number;
  nokPerCurrencyUnit: number;
  onChange: (check: CheckState) => void;
}

// The parent keys this controller by organization, amount and retry count so
// every new check owns a fresh hook and cancels the previous request on unmount.
export function DeMinimisCheckController({
  organizationNumber, requestedAmount, threshold, nokPerCurrencyUnit, onChange,
}: Props) {
  const { checkDeMinimis, result, error, reset } = useDeMinimisCheck();

  useEffect(() => {
    const valid = /^\d{9}$/.test(organizationNumber) &&
      Number.isFinite(requestedAmount) && requestedAmount >= 0;
    onChange({
      organizationNumber,
      status: valid ? 'checking' : 'unavailable',
      previousSupport: 0,
      requestedAmount,
      totalAmount: requestedAmount,
      difference: 0,
      isOverThreshold: false,
    });
    if (!valid) return;
    const timeout = window.setTimeout(() => {
      void checkDeMinimis(organizationNumber, requestedAmount * nokPerCurrencyUnit);
    }, 300);
    return () => {
      window.clearTimeout(timeout);
      reset();
    };
  }, [checkDeMinimis, nokPerCurrencyUnit, onChange, organizationNumber, requestedAmount, reset]);

  useEffect(() => {
    if (!result && !error) return;
    if (result && (result.organizationNumber !== organizationNumber ||
        result.requestedAmount !== requestedAmount * nokPerCurrencyUnit)) return;
    const previousSupport = result
      ? result.previousSupport / nokPerCurrencyUnit
      : 0;
    const totalAmount = previousSupport + requestedAmount;
    const difference = threshold - totalAmount;
    onChange({
      organizationNumber,
      status: result ? 'verified' : 'unavailable',
      previousSupport,
      requestedAmount,
      totalAmount,
      difference,
      isOverThreshold: difference < 0,
      checkedAt: result?.checkedAt,
      error: error ?? undefined,
    });
  }, [error, nokPerCurrencyUnit, onChange, organizationNumber, requestedAmount, result, threshold]);

  return null;
}
